import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Film, Zap, Sparkles, Loader2, ChevronRight, RefreshCcw } from "lucide-react";
import api from "@/lib/api";
import DepartmentPills, { PILLAR_META, BAND_COLORS } from "@/components/DepartmentPills";

/**
 * DepartmentVerdictBoard — the daily intelligence roll-up.
 *
 * ArmIQ IS the Athlete Performance Operating System™. This board is the
 * staff-meeting whiteboard: one row per athlete, worst-band-first, with
 * the four intelligence pillars distilled into the three staff-facing
 * pillars (Availability · Development · Performance) + cross-module
 * cause analysis explaining every recommendation.
 *
 * Embeds inline on the Coach Overview (top of /dashboard). Not a
 * standalone page — the entire ArmIQ product behaves as the intelligence
 * layer connecting every technology, every department, every athlete.
 */
export default function DepartmentVerdictBoard({ title = "Today's intelligence layer", subtitle = "Sorted worst-first — who needs you today." }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  async function load() {
    setBusy(true);
    try {
      const { data } = await api.get("/department/roster");
      setRows(data.items || []);
    } catch {
      setRows([]);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] overflow-hidden" data-testid="dept-verdict-board">
      <div className="px-5 py-3 border-b border-[var(--ink-border)] flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold"
               style={{ color: "var(--brand-secondary)" }}>
            <Sparkles className="h-3 w-3" /> Three pillars · Availability · Development · Performance
          </div>
          <div className="font-display font-bold text-white text-lg mt-0.5" data-testid="dept-board-title">{title}</div>
          <div className="text-[11px] text-white/45">{subtitle}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/40" data-testid="dept-board-count">{rows?.length || 0} athlete{(rows?.length || 0) === 1 ? "" : "s"}</span>
          <Button onClick={load} variant="outline" size="sm" className="border-white/15 text-white/75 bg-white/5 hover:bg-white/10 h-7 px-2" data-testid="dept-refresh">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3" />}
          </Button>
        </div>
      </div>

      {rows == null && (
        <div className="px-5 py-10 text-center text-white/45 text-sm" data-testid="dept-loading">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Reading the room…
        </div>
      )}
      {rows?.length === 0 && (
        <div className="px-5 py-10 text-center text-white/45 text-sm" data-testid="dept-empty">
          No pitchers on the roster yet. Send invites from the Roster page to start the department.
        </div>
      )}
      {rows?.length > 0 && (
        <ul className="divide-y divide-[var(--ink-border)]" data-testid="dept-verdict-list">
          {rows.map((v) => (
            <VerdictRow
              key={v.player_user_id}
              v={v}
              expanded={selected === v.player_user_id}
              onToggle={() => setSelected(selected === v.player_user_id ? null : v.player_user_id)}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}


function VerdictRow({ v, expanded, onToggle }) {
  const overall = BAND_COLORS[v.overall_band] || BAND_COLORS.hold;
  return (
    <li className="px-5 py-4" data-testid={`dept-row-${v.player_user_id}`}>
      <div className="flex items-center gap-4">
        <button onClick={onToggle} className="flex-1 min-w-0 text-left group" data-testid={`dept-row-toggle-${v.player_user_id}`}>
          <div className="flex items-center gap-3">
            <span className="font-display font-bold text-white truncate text-base group-hover:underline">{v.player_name || "—"}</span>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold border"
                  style={{ backgroundColor: overall.bg, borderColor: overall.border, color: overall.text }}
                  data-testid={`dept-row-band-${v.player_user_id}`}>
              {overall.label}
            </span>
            <span className="text-[11px] text-white/45 font-mono-num">{Math.round(v.overall_score)}</span>
          </div>
          <div className="mt-2">
            <DepartmentPills verdict={v} testId={`dept-row-pills-${v.player_user_id}`} />
          </div>
        </button>
        <Link to={`/dashboard/player/${v.player_user_id}`} className="text-[11px] text-white/55 hover:text-white inline-flex items-center gap-0.5"
              data-testid={`dept-row-profile-${v.player_user_id}`}>
          Profile <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
      {expanded && <VerdictDetail v={v} />}
    </li>
  );
}

function VerdictDetail({ v }) {
  return (
    <div className="mt-4 pl-1 grid lg:grid-cols-3 gap-3" data-testid={`dept-detail-${v.player_user_id}`}>
      {["availability", "development", "performance"].map((k) => {
        const p = v.pillars[k];
        const m = PILLAR_META[k];
        const Icon = m.Icon;
        return (
          <div key={k} className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`dept-detail-${k}-${v.player_user_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-7 h-7 rounded flex items-center justify-center" style={{ backgroundColor: `${m.color}1c`, color: m.color }}>
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div>
                <div className="text-xs font-bold text-white">{m.label}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/40">{p.band} · {Math.round(p.score)}</div>
              </div>
            </div>
            <ul className="space-y-1.5">
              {(p.drivers || []).map((d, i) => {
                const isBackfill = d.module === "backfill_import";
                return (
                  <li
                    key={i}
                    className={`text-[11px] flex items-start justify-between gap-2 ${isBackfill ? "rounded-md border px-2 py-1.5" : "text-white/70"}`}
                    style={isBackfill ? { backgroundColor: "rgba(34,211,238,0.06)", borderColor: "rgba(34,211,238,0.28)" } : undefined}
                    data-testid={`dept-driver-${k}-${i}-${v.player_user_id}`}
                  >
                    <span className={`flex items-start gap-1.5 ${isBackfill ? "text-cyan-200" : ""}`}>
                      {isBackfill && (
                        <span className="text-[9px] tracking-[0.18em] uppercase font-bold px-1.5 py-0.5 rounded mt-0.5 shrink-0"
                              style={{ backgroundColor: "rgba(34,211,238,0.18)", color: "#22D3EE" }}>
                          Backfill
                        </span>
                      )}
                      <span>{d.signal}</span>
                    </span>
                    {isBackfill ? (
                      <span className="text-[10px] font-bold text-cyan-300/70 whitespace-nowrap">info</span>
                    ) : (
                      <span className={`font-mono-num text-[10px] font-bold ${d.delta > 0 ? "text-emerald-300" : d.delta < 0 ? "text-amber-300" : "text-white/35"}`}>
                        {d.delta > 0 ? "+" : ""}{d.delta}
                      </span>
                    )}
                  </li>
                );
              })}
              {(p.drivers || []).length === 0 && (
                <li className="text-[11px] text-white/40">No active drivers.</li>
              )}
            </ul>
          </div>
        );
      })}
      <div className="lg:col-span-3" data-testid={`dept-cause-${v.player_user_id}`}>
        {v.cause_analysis?.length > 0 ? (
          <div className="rounded-lg border p-4"
               style={{ borderColor: "rgba(217,4,41,0.35)", backgroundColor: "rgba(217,4,41,0.06)" }}>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: "var(--brand-secondary)" }}>
              Cross-module cause analysis · v0
            </div>
            <ul className="space-y-3">
              {v.cause_analysis.map((c) => (
                <li key={c.rule} className="text-sm" data-testid={`dept-cause-rule-${c.rule}`}>
                  <div className="font-bold text-white">{c.title}</div>
                  <div className="text-[12px] text-white/75 leading-relaxed mt-1">{c.narrative}</div>
                  <div className="text-[11px] text-white/60 mt-1.5">
                    <b style={{ color: "var(--brand-secondary)" }}>Next step:</b> {c.next_step}
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-white/35 mt-1">
                    {c.modules.join(" × ")}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-[11px] text-white/35 italic px-2" data-testid={`dept-cause-empty-${v.player_user_id}`}>
            No cross-module cause patterns detected — all pillars within their own bands.
          </div>
        )}
      </div>
    </div>
  );
}
